import { inGamut } from "./gamut";
import type { Rgba } from "./rgba";
import { toOklab, toPolar } from "./spaces";

export interface Swatch {
  colour: Rgba;
  // The colour the page is actually holding, which every row is built around and one swatch in each row is.
  base: boolean;
}

export interface Harmony {
  id: string;
  label: string;
  // Degrees around the OKLCH hue circle, with the colour itself sitting at zero wherever the arrangement puts it.
  angles: number[];
}

// The classical arrangements, each measured on the hue circle: the angles are what the arrangement is.
export const HARMONIES: Harmony[] = [
  { id: "complementary", label: "Complementary", angles: [0, 180] },
  { id: "analogous", label: "Analogous", angles: [-30, 0, 30] },
  { id: "triadic", label: "Triadic", angles: [0, 120, 240] },
  { id: "split", label: "Split complementary", angles: [0, 150, 210] },
  { id: "tetradic", label: "Tetradic", angles: [0, 90, 180, 270] },
];

// A partner is the colour's own lightness and chroma at another hue, so a harmony reads as one family rather than as
// five colours that happen to share a page. Turning the hue in HSL instead is what makes a yellow partner glare and a
// blue one go muddy: HSL's lightness is arithmetic on the channels and says nothing about how light anything looks.
export function harmony(colour: Rgba, angles: number[]): Swatch[] {
  const [l, c, h] = toPolar(toOklab(colour), 0);
  return angles.map((angle) => ({ colour: inGamut(l, c, h + angle, colour.a), base: angle === 0 }));
}

// Even steps in OKLab lightness, where a step of the same size looks the same size wherever it is taken, so the ramp
// reads as evenly spaced rather than piling up at the dark end the way an HSL one does.
export const TONE_STEPS = [0.95, 0.85, 0.75, 0.65, 0.55, 0.45, 0.35, 0.25, 0.15];

export function tones(colour: Rgba): Swatch[] {
  const [l, c, h] = toPolar(toOklab(colour), 0);
  const nearest = TONE_STEPS.reduce((best, step) => Math.abs(step - l) < Math.abs(best - l) ? step : best);
  return TONE_STEPS.map((step) => ({ colour: inGamut(step, c, h, colour.a), base: step === nearest }));
}
